import { Injectable, inject } from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {environment} from '@environments/environment';
import {
  WidgetCustomizationAiRequest,
  WidgetCustomizationAiResult
} from '@app/_components/widget-customization-ai/widget-customization-ai.types';

@Injectable({
  providedIn: 'root'
})
export class ProjectService {
  private http = inject(HttpClient);

  private baseApiUrl = environment.apiUrl;
  private projectUrl = this.baseApiUrl + '/api/v1/project';
  private projectNotificationUrl = this.baseApiUrl + '/api/v1/notification/settings';
  private projectIndustryUrl = this.baseApiUrl + '/api/v1/project_industry';
  private projectCurrenciesUrl = this.projectUrl + '/currencies';

  getCurrencies() {
    return this.http.get(`${this.projectCurrenciesUrl}`);
  }

  getAllProjects(params: any = {}) {
    return this.http.get(`${this.projectUrl}`, {params,  headers: new HttpHeaders().set('Use-Temp-Token', 'true')});
  }

  update(item, id: number) {
    return this.http.put(`${this.projectUrl}/${id}`, item);
  }

  getIndustries() {
    return this.http.get(`${this.projectIndustryUrl}`);
  }

  getById(id: number) {
    return this.http.get(`${this.projectUrl}/${id}`);
  }

  validateProjectSlug(params) {
    return this.http.post(`${this.projectUrl}/validate_slug`, params);
  }

  create(item) {
    return this.http.post(`${this.projectUrl}`, item);
  }

  /**
   * `type` picks the request filed once this project is deleted: 'anonymize' (the default)
   * clears what identifies it and its advocates and gives it back once approved, so reports
   * and year-over-year comparison keep working; 'delete' hides it immediately and permanently
   * deletes it once approved.
   */
  deleteProject(projectId, type: 'anonymize' | 'delete' = 'anonymize', anonymizeScope: ReadonlyArray<string> | null = null) {
    const params: { [key: string]: string } = {type};
    if (type === 'anonymize' && anonymizeScope && anonymizeScope.length) {
      params.scope = anonymizeScope.join(',');
    }
    return this.http.delete(`${this.projectUrl}/${projectId}`, {params});
  }

  /**
   * Who will be emailed to approve this project's deletion, asked before the user
   * commits rather than told afterwards. Also reports whether a request is already
   * open for the project.
   */
  getDeletionPreview(projectId: number) {
    return this.http.get<any>(`${this.projectUrl}/${projectId}/deletion-preview`);
  }

  projectCopy(projectId: number, body: any) {
    return this.http.post(`${this.projectUrl}/${projectId}/copy`, body);
  }

  projectsReportExport(params: any = {}) {
    return this.http.post(`${this.projectUrl}/report/export`, params);
  }

  notificationUpdate(item, id: number) {
    return this.http.put(`${this.projectNotificationUrl}?id=${id}`, item);
  }

  ProjectPlanners(projectId: number) {
    return this.http.get(`${this.projectUrl}/${projectId}/planner`);
  }

  projectDemo(projectId: number, body: any) {
    return this.http.post(`${this.projectUrl}/${projectId}/demo`, body);
  }

  generateWidgetCustomization(projectId: number, payload: WidgetCustomizationAiRequest) {
    return this.http.post<{success: boolean; data: WidgetCustomizationAiResult}>(
      `${this.projectUrl}/${projectId}/widget-customization/ai/generate`,
      payload
    );
  }

  getWidgetLanguages() {
    return this.http.get<{ success: boolean; data: { value: string; label: string }[] }>(
      `${this.baseApiUrl}/api/v1/share/widget_languages`
    );
  }
}
